// ============================================================
// Personel maaş, avans ve bordro (§10)
//
// İlke, fatura tarafındakiyle aynı: bordro tutarı kayıtta saklanmaz,
// dönemin maaş sözleşmesinden hesaplanır. Zam geçmişi ayrı tutulduğu
// için geçmiş bordro her zaman o dönemin maaşıyla yeniden üretilebilir.
//
// Tek istisna: ÖDENMİŞ tutar. O gerçekleşmiş bir olaydır, sonradan
// maaş değişse bile değişmemelidir.
// ============================================================

import { DONEM } from "../config.js";
import { KESINTI_VARSAYILAN } from "../data/tanimlar.js";
import { B, subeBulHerYerde } from "./context.js";


const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

// ---------- Maaş ----------

/**
 * Belirli bir dönemde geçerli olan brüt maaş.
 * Zam geçmişi varsa oradan, yoksa güncel sözleşmeden okunur.
 */
export function donemMaasi(kisi, period = DONEM) {
  const gecmis = kisi.compensationHistory ?? [];
  const kayit = gecmis.find(h =>
    h.effectiveFrom.slice(0, 7) <= period &&
    (!h.effectiveTo || h.effectiveTo.slice(0, 7) >= period));

  if (kayit) return kayit.gross;

  const c = kisi.compensation;
  if (!c) return 0;
  if (c.type === "seans") return (c.seansUcreti ?? 0) * (c.seansSayisi ?? 0);
  return c.gross ?? 0;
}

export const yanOdemeler = kisi =>
  (kisi.compensation?.allowances ?? []).reduce((a, x) => a + x.amount, 0);

// ---------- Avanslar ----------

export const avanslar = branchId => sube(branchId).staffAdvances ?? [];

export const personelAvanslari = (staffId, branchId) =>
  avanslar(branchId).filter(a => a.staffId === staffId);

/** Ödenmiş ama henüz mahsup edilmemiş avanslar. */
export const acikAvanslar = branchId =>
  avanslar(branchId).filter(a => a.paidAt && !a.settledAt);

/** Onay bekleyen talepler. */
export const bekleyenAvanslar = branchId =>
  avanslar(branchId).filter(a => !a.approvedBy);

/** Bir personelin açık avans bakiyesi. */
export function avansBakiyesi(staffId, branchId) {
  return acikAvanslar(branchId)
    .filter(a => a.staffId === staffId)
    .reduce((toplam, a) => toplam + kalanAvans(a), 0);
}

/** Taksitli avansta bir dönemde kesilecek tutar. */
export const taksitTutari = a => Math.round(a.amount / (a.installments || 1));

/** Avansın kalan borcu — taksitli ise tamamı henüz kesilmemiştir. */
export const kalanAvans = a => (a.settledAt ? 0 : a.amount);

/** Bu dönem bordrodan kesilecek avans tutarı. */
export function donemAvansKesintisi(staffId, branchId, period = DONEM) {
  return acikAvanslar(branchId)
    .filter(a => a.staffId === staffId && a.deductPeriod <= period)
    .reduce((t, a) => t + taksitTutari(a), 0);
}

// ---------- Bordro ----------

/**
 * Bir personelin dönem bordrosunu hesaplar.
 * Kesinti oranları şube ayarlarından gelir; koda gömülü değil.
 */
export function bordroSatiri(kisi, branchId, period = DONEM) {
  const t = sube(branchId);
  const o = t.payrollConfig ?? KESINTI_VARSAYILAN;

  const brut = donemMaasi(kisi, period);
  const yanOdeme = yanOdemeler(kisi);

  const additions = [
    ...(kisi.compensation?.allowances ?? []).map(x => ({ label: x.label, amount: x.amount })),
  ];

  const avansKesinti = donemAvansKesintisi(kisi.id, branchId, period);

  const deductions = [
    { label: "Sosyal Sigorta", amount: Math.round(brut * o.sosyalSigorta), rate: o.sosyalSigorta },
    { label: "İhtiyat Sandığı", amount: Math.round(brut * o.ihtiyatSandigi), rate: o.ihtiyatSandigi },
    { label: "Gelir Vergisi", amount: Math.round(brut * o.gelirVergisi), rate: o.gelirVergisi },
  ];

  if (avansKesinti > 0) {
    deductions.push({ label: "Avans mahsubu", amount: avansKesinti });
  }

  // Personel çocuğu indiriminin kuruma maliyeti personel maliyetinin
  // bir parçasıdır; bordroda ayrı kalem olarak görünür (kesinti değil,
  // bilgi amaçlı — tutar sıfır, etiket taşır).
  const cocuk = t.students.find(s => s.parent === kisi.name && s.discount?.label === "Personel çocuğu");

  const toplamEk = additions.reduce((a, x) => a + x.amount, 0);
  const toplamKesinti = deductions.reduce((a, x) => a + x.amount, 0);
  const net = brut + toplamEk - toplamKesinti;

  const isverenMaliyeti = brut + toplamEk
    + Math.round(brut * o.isverenSosyalSigorta)
    + Math.round(brut * o.isverenIhtiyatSandigi);

  return {
    staffId: kisi.id,
    name: kisi.name,
    role: kisi.role,
    gross: brut,
    yanOdeme,
    additions,
    deductions,
    toplamEk,
    toplamKesinti,
    net,
    isverenMaliyeti,
    personelCocugu: cocuk ? { studentId: cocuk.id, name: cocuk.name, pct: cocuk.discount.pct } : null,
  };
}

/** Şubenin dönem bordrosu. */
export function bordroUret(branchId, period = DONEM) {
  const t = sube(branchId);
  const items = t.staff
    .filter(k => k.status !== "ayrildi")
    .map(k => bordroSatiri(k, t.id, period))
    .sort((a, b) => b.gross - a.gross);

  return {
    period,
    branchId: t.id,
    status: t.payroll?.[period]?.status ?? "taslak",
    items,
    totalGross: items.reduce((a, x) => a + x.gross, 0),
    totalNet: items.reduce((a, x) => a + x.net, 0),
    totalDeductions: items.reduce((a, x) => a + x.toplamKesinti, 0),
    totalEmployerCost: items.reduce((a, x) => a + x.isverenMaliyeti, 0),
  };
}

/** Bordro onaylandıktan sonra kilitlenir; düzeltme ters kayıtla yapılır. */
export const bordroKilitli = (branchId, period = DONEM) =>
  (sube(branchId).payroll?.[period]?.status ?? "taslak") !== "taslak";
